import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { Zap, Image, Music, X, Loader2, CheckCircle, Play, Download, Eye, Plus, RefreshCw, Link2 } from 'lucide-react';
import { downloadFile } from '../../utils/downloadFile';
import { blobToFile } from '../../utils/mediaUtils';
import { useWorkflowStore } from '../../store/workflowStore';
import { useNodeExecution } from '../../hooks/useNodeExecution';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import NodeError from './NodeError';
import NodeResetButton from './NodeResetButton';
import {
  ReconfigureButton, ReconfigureNotice, useReconfigure,
} from './ReconfigureControls';
import { useMediaPreview } from '../../hooks/useMediaPreview';
import { setSourcePickerHandler } from '../../utils/sourcePickerBridge';
import SendToTimelineButton from '../SendToTimelineButton';
import DirectorsTrigger from '../studio/DirectorsTrigger';
import PromptField from '../studio/PromptField';
import NodeRequirements from './NodeRequirements';
import { missingOf, requirementsTitle, inputNeed, need } from '../../utils/nodeRequirements';

// Listas EXACTAS del schema de prunaai/p-video-avatar (verificadas contra la API).
const VOICES = [
  'Zephyr (Female)', 'Puck (Male)', 'Charon (Male)', 'Kore (Female)',
  'Fenrir (Male)', 'Leda (Female)', 'Orus (Male)', 'Aoede (Female)',
  'Callirrhoe (Female)', 'Autonoe (Female)', 'Enceladus (Male)', 'Iapetus (Male)',
  'Umbriel (Male)', 'Algenib (Male)', 'Despina (Female)', 'Erinome (Female)',
  'Laomedeia (Female)', 'Achernar (Female)', 'Algieba (Male)', 'Schedar (Male)',
  'Gacrux (Female)', 'Pulcherrima (Female)', 'Achird (Male)', 'Zubenelgenubi (Male)',
  'Vindemiatrix (Female)', 'Sadachbia (Male)', 'Sadaltager (Male)', 'Sulafat (Female)',
  'Alnilam (Male)', 'Rasalgethi (Male)',
];

const LANGUAGES = [
  'English (US)', 'English (UK)', 'Spanish', 'French', 'German',
  'Italian', 'Portuguese (Brazil)', 'Japanese', 'Korean', 'Hindi',
];

const IMAGE_ACCEPT = 'image/jpeg,image/jpg,image/png,image/webp';
const AUDIO_ACCEPT = 'audio/mpeg,audio/mp3,audio/wav,audio/m4a,audio/aac,audio/ogg';

const AvatarFastNode = memo(({ id, data, selected }) => {
  const [chronometer, setChronometer] = useState(0);
  const [imageLocal, setImageLocal] = useState(data.imageLocal || null);
  const [audioLocal, setAudioLocal] = useState(data.audioLocal || null);
  const [resolution, setResolution] = useState(data.resolution || '720p');
  const [voice, setVoice] = useState(data.voice || 'Zephyr (Female)');
  const [voiceLanguage, setVoiceLanguage] = useState(data.voiceLanguage || 'Spanish');
  const [voiceScript, setVoiceScript] = useState(data.voiceScript || '');
  const [voicePrompt, setVoicePrompt] = useState(data.voicePrompt || '');
  const [videoPrompt, setVideoPrompt] = useState(data.videoPrompt || '');
  const [seed, setSeed] = useState(data.seed ?? '');
  const [negativePrompt, setNegativePrompt] = useState(data.negativePrompt || '');
  const [strengthNegativePrompt, setStrengthNegativePrompt] = useState(data.strengthNegativePrompt ?? 0.5);
  const [disableSafetyFilter, setDisableSafetyFilter] = useState(data.disableSafetyFilter ?? true);
  const [disablePromptUpsampling, setDisablePromptUpsampling] = useState(data.disablePromptUpsampling ?? false);
  const [localError, setLocalError] = useState(null);

  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const addAsset = useWorkflowStore((state) => state.addAsset);
  const edges = useWorkflowStore((state) => state.edges);
  const nodes = useWorkflowStore((state) => state.nodes);
  const openSourcePicker = useWorkflowStore((state) => state.openSourcePicker);

  const { processNode, status: execStatus, error: execError, reset: resetExec, insufficientCredits } = useNodeExecution();
  const { openVideoPreview } = useMediaPreview();

  const isProcessing = execStatus === 'uploading' || execStatus === 'queued' || execStatus === 'processing';

  const connData = useCallback((handleId) => {
    const edge = edges.find(e => e.target === id && e.targetHandle === handleId);
    if (!edge) return null;
    const src = nodes.find(n => n.id === edge.source);
    return src?.data?.hasOutput ? src.data : null;
  }, [edges, nodes, id]);

  const imageConn = connData('inputImage');
  const audioConn = connData('inputAudio');
  const image = imageConn || imageLocal;
  const audio = audioConn || audioLocal;

  useEffect(() => {
    updateNodeData(id, {
      resolution, voice, voiceLanguage, voiceScript, voicePrompt, videoPrompt,
      seed, negativePrompt, strengthNegativePrompt, disableSafetyFilter,
      disablePromptUpsampling, imageLocal, audioLocal,
    });
  }, [resolution, voice, voiceLanguage, voiceScript, voicePrompt, videoPrompt,
      seed, negativePrompt, strengthNegativePrompt, disableSafetyFilter,
      disablePromptUpsampling, imageLocal, audioLocal]); // eslint-disable-line

  useEffect(() => {
    let interval;
    if (isProcessing) {
      setChronometer(0);
      interval = setInterval(() => setChronometer((prev) => prev + 1), 1000);
    }
    return () => { if (interval) clearInterval(interval); };
  }, [isProcessing]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const pickMedia = useCallback((which) => {
    const isImage = which === 'image';
    setSourcePickerHandler((result) => {
      const entry = result.file
        ? { file: result.file, preview: URL.createObjectURL(result.file), name: result.file.name }
        : { file: null, preview: result.url, name: result.fileName, fromGallery: true };
      if (isImage) setImageLocal(entry); else setAudioLocal(entry);
    });
    openSourcePicker(id, [which], isImage ? IMAGE_ACCEPT : AUDIO_ACCEPT);
  }, [id, openSourcePicker]);

  // Regenerar descarta el resultado; volver a configurar lo conserva.
  const { reconfigure, setReconfigure, showForm } = useReconfigure(data);

  const handleProcess = useCallback(async () => {
    if (!image) {
      setLocalError('Falta la imagen del rostro: conectá un nodo de imagen o cargá un retrato (jpg/png/webp).');
      return false;
    }
    const script = voiceScript.trim();
    // Regla del modelo: si hay audio, tiene prioridad y reemplaza guion/voz/idioma.
    if (!audio && !script) {
      setLocalError('Cargá un audio o escribí el guion exacto que debe decir el avatar.');
      return false;
    }
    setLocalError(null);
    resetExec();

    const files = [];
    const params = {
      resolution,
      voice,
      voice_language: voiceLanguage,
      disable_safety_filter: disableSafetyFilter,
      disable_prompt_upsampling: disablePromptUpsampling,
    };

    const attach = async (src, key, mime) => {
      let file = src.file;
      if (!file) {
        const url = src.outputUrl || src.preview;
        if (url?.startsWith('http')) { params[key] = url; return; }
        if (url) file = await blobToFile(url, src.name || `${key}.bin`, mime);
      }
      if (file) files.push({ key, file });
    };
    await attach(image, 'image_url', 'image/jpeg');
    if (audio) {
      await attach(audio, 'audio_url', 'audio/mpeg');
    } else {
      params.voice_script = script;
      if (voicePrompt.trim()) params.voice_prompt = voicePrompt.trim();
    }
    if (videoPrompt.trim()) params.video_prompt = videoPrompt.trim();
    if (seed !== '' && seed !== null && seed !== undefined) {
      const parsed = parseInt(seed, 10);
      if (!Number.isNaN(parsed)) params.seed = parsed;
    }
    if (negativePrompt.trim()) {
      params.negative_prompt = negativePrompt.trim();
      params.strength_negative_prompt = Number(strengthNegativePrompt);
    }

    const result = await processNode({
      resultType: 'avatar_fast',
      files,
      params,
    });

    if (result?.outputUrl) {
      setReconfigure(false);
      updateNodeData(id, {
        hasOutput: true,
        outputUrl: result.outputUrl,
        assetId: result.assetId ?? null,
        outputType: 'video',
        processedAt: new Date().toISOString(),
      });
      addAsset({
        id: `asset_${Date.now()}`,
        type: 'video',
        tool: 'avatarFast',
        filename: `avatarfast_${Date.now()}.mp4`,
        url: result.outputUrl,
        prompt: audio ? `Avatar (lip-sync a audio)` : script.slice(0, 120),
        createdAt: new Date().toISOString(),
      });
      return true;
    }
    return false;
  }, [id, image, audio, voiceScript, voicePrompt, videoPrompt, resolution, voice,
      voiceLanguage, seed, negativePrompt, strengthNegativePrompt, disableSafetyFilter,
      disablePromptUpsampling, updateNodeData, addAsset, processNode, resetExec, setReconfigure]);

  const handleRegenerate = useCallback(() => {
    updateNodeData(id, { hasOutput: false, outputUrl: null });
    resetExec();
  }, [id, updateNodeData, resetExec]);

  const { isInQueue, isMyTurn } = useWorkflowTrigger(id, handleProcess);

  const canProcess = !!image && (!!audio || voiceScript.trim().length > 0);

  // NR-1 · qué le falta al nodo para generar (se muestra debajo del botón)
  const runMissing = missingOf([inputNeed('image', !!image), need(!!audio || voiceScript.trim().length > 0, 'un audio o el texto que va a decir el avatar')]);

  return (
    <div className={`node-container avatarFast-node ${selected ? 'selected' : ''} ${isInQueue ? 'workflow-queued' : ''} ${isMyTurn ? 'workflow-active' : ''}`} data-testid="avatarfast-node">
      <NodeResetButton nodeId={id} hasOutput={!!data.hasOutput} />

      {/* Input handles: imagen (requerida) + audio (opcional) */}
      <Handle type="target" position={Position.Left} id="inputImage" className="node-handle input-handle" style={{ top: '25%' }} data-testid="avatarfast-input-image" />
      <Handle type="target" position={Position.Left} id="inputAudio" className="node-handle input-handle audio-handle" style={{ top: '45%' }} data-testid="avatarfast-input-audio" />

      {/* Header */}
      <div className="node-header avatarFast-header">
        <div className="node-header-left">
          <Zap size={16} />
          <span>Avatar Fast</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)} data-testid="delete-avatarfast">
          <X size={14} />
        </button>
      </div>

      {/* Content */}
      <div className="node-content avatarFast-content">
        {isProcessing ? (
          <div className="node-processing">
            <div className="node-chronometer">{formatTime(chronometer)}</div>
            <div className="node-processing-info">
              <Loader2 size={16} className="animate-spin" />
              <span>
                {execStatus === 'uploading' ? 'Subiendo archivos...' :
                 execStatus === 'queued' ? 'En cola...' :
                 'Animando avatar...'}
              </span>
            </div>
            <p className="node-processing-hint">{resolution} · {audio ? 'lip-sync a audio' : `voz ${voice}`}</p>
          </div>
        ) : !showForm ? (
          /* ===== RESULT VIEW ===== */
          <div className="node-result">
            <video
              src={data.outputUrl}
              controls
              className="node-result-video clickable"
              onClick={() => openVideoPreview(data.outputUrl, 'Avatar Fast')}
            />
            <div className="node-result-actions">
              <button className="node-mini-preview-btn" onClick={() => openVideoPreview(data.outputUrl, 'Avatar Fast')} data-testid="avatarfast-preview-btn">
                <Eye size={12} /> Ver
              </button>
              <button className="node-mini-download-btn" onClick={(e) => { e.stopPropagation(); downloadFile(data.outputUrl, `avatarfast_${Date.now()}.mp4`); }} data-testid="avatarfast-download-btn">
                <Download size={12} /> Descargar
              </button>
              <SendToTimelineButton assetId={data.assetId} nodeId={id} url={data.outputUrl} kind="video" label="avatarFast" testIdPrefix="avatarfast-timeline" />
            </div>
            <div className="nano-regen-section">
              <button className="nano-regen-btn avatarFast-regen-btn" onClick={handleRegenerate} data-testid="avatarfast-regen-btn">
                <RefreshCw size={12} />
                Regenerar
              </button>
              <ReconfigureButton onClick={() => setReconfigure(true)} testId="avatarfast-reconfigure-btn" />
            </div>
            <div className="node-result-badge">
              <CheckCircle size={12} />
              <span>Avatar generado</span>
            </div>
          </div>
        ) : (
          <>
            {data.outputUrl && reconfigure && (
              <ReconfigureNotice
                previewUrl={null}
                onCancel={() => setReconfigure(false)}
                testId="avatarfast-reconfigure-notice"
              />
            )}

            {/* Imagen del rostro (requerida) */}
            <div className="nano-images-section">
              <span className="nano-section-label">
                <Image size={12} />
                Retrato (rostro frontal) *
              </span>
              <div className="nano-images-row">
                {image ? (
                  <div className="nano-img-thumb avatarFast-media-thumb" title={image.name || 'imagen'}>
                    {image.preview ? (
                      <img src={image.outputUrl || image.preview} alt="Retrato" className="avatarFast-thumb-img" />
                    ) : (
                      <Image size={16} />
                    )}
                    {!imageConn && (
                      <button className="nano-clear-btn" onClick={() => setImageLocal(null)} data-testid="avatarfast-clear-image">
                        <X size={8} />
                      </button>
                    )}
                  </div>
                ) : (
                  <button className="nano-add-img-btn" onClick={() => pickMedia('image')} title="Subir o elegir imagen de la galería" data-testid="avatarfast-add-image">
                    <Plus size={14} />
                  </button>
                )}
              </div>
              {imageConn && <span className="avatarFast-conn-note"><Link2 size={10} /> Imagen conectada</span>}
            </div>

            {/* Audio opcional (tiene prioridad sobre el guion) */}
            <div className="nano-images-section">
              <span className="nano-section-label">
                <Music size={12} />
                Audio (opcional — reemplaza al guion)
              </span>
              <div className="nano-images-row">
                {audio ? (
                  <div className="nano-img-thumb avatarFast-media-thumb" title={audio.name || 'audio'}>
                    <Music size={16} />
                    {!audioConn && (
                      <button className="nano-clear-btn" onClick={() => setAudioLocal(null)} data-testid="avatarfast-clear-audio">
                        <X size={8} />
                      </button>
                    )}
                  </div>
                ) : (
                  <button className="nano-add-img-btn" onClick={() => pickMedia('audio')} title="Subir o elegir audio de la galería" data-testid="avatarfast-add-audio">
                    <Plus size={14} />
                  </button>
                )}
              </div>
              {audioConn && <span className="avatarFast-conn-note"><Link2 size={10} /> Audio conectado</span>}
              {audio && (
                <span className="avatarFast-priority-note" data-testid="avatarfast-audio-priority">
                  El audio tiene prioridad: se ignoran guion, voz e idioma.
                </span>
              )}
            </div>

            {/* Guion + voz + idioma (sólo sin audio) */}
            {!audio && (
              <>
                <div className="nano-prompt-section">
                  <div className="nano-prompt-header">
                    <span className="nano-section-label">Guion (lo que dice) *</span>
                    <DirectorsTrigger nodeId={id} nodeType="avatarFast" currentPrompt={voiceScript} onInsert={setVoiceScript} />
                  </div>
                  <PromptField
                    value={voiceScript}
                    onChange={setVoiceScript}
                    nodeId={id}
                    nodeType="avatarFast"
                    placeholder="Las palabras exactas que el avatar debe decir..."
                    className="nano-prompt-textarea"
                    rows={3}
                    testId="avatarfast-script"
                  />
                </div>

                <div className="nano-options-row avatarFast-options">
                  <div className="nano-option">
                    <span className="nano-option-label">Voz</span>
                    <select value={voice} onChange={(e) => setVoice(e.target.value)} className="nano-select" data-testid="avatarfast-voice">
                      {VOICES.map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  </div>
                  <div className="nano-option">
                    <span className="nano-option-label">Idioma</span>
                    <select value={voiceLanguage} onChange={(e) => setVoiceLanguage(e.target.value)} className="nano-select" data-testid="avatarfast-language">
                      {LANGUAGES.map((l) => (
                        <option key={l} value={l}>{l}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="nano-prompt-section">
                  <span className="nano-section-label">Estilo del habla (opcional)</span>
                  <textarea
                    value={voicePrompt}
                    onChange={(e) => setVoicePrompt(e.target.value)}
                    placeholder="Tono, ritmo, emoción... (no se pronuncia)"
                    className="nano-prompt-textarea avatarFast-mini-textarea"
                    rows={2}
                    data-testid="avatarfast-voice-prompt"
                  />
                </div>
              </>
            )}

            {/* Acción visual */}
            <div className="nano-prompt-section">
              <span className="nano-section-label">Acción visual (opcional)</span>
              <textarea
                value={videoPrompt}
                onChange={(e) => setVideoPrompt(e.target.value)}
                placeholder="Ej: la persona gesticula con las manos mientras habla..."
                className="nano-prompt-textarea avatarFast-mini-textarea"
                rows={2}
                data-testid="avatarfast-video-prompt"
              />
            </div>

            {/* Resolución */}
            <div className="fabric-settings">
              <div className="fabric-setting">
                <label>
                  <Eye size={12} />
                  Resolución
                </label>
                <div className="fabric-options">
                  {['720p', '1080p'].map((r) => (
                    <button
                      key={r}
                      className={`fabric-option ${resolution === r ? 'active' : ''}`}
                      onClick={() => setResolution(r)}
                      data-testid={`avatarfast-resolution-${r}`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Negative prompt + fuerza */}
            <div className="nano-prompt-section">
              <span className="nano-section-label">Negative prompt (opcional)</span>
              <textarea
                value={negativePrompt}
                onChange={(e) => setNegativePrompt(e.target.value)}
                placeholder="subtítulos, texto, borroso, watermark..."
                className="nano-prompt-textarea avatarFast-mini-textarea"
                rows={2}
                data-testid="avatarfast-negative-prompt"
              />
            </div>
            {negativePrompt.trim() && (
              <div className="eleV2-slider-row avatarFast-strength-row">
                <span className="nano-option-label">Fuerza negative</span>
                <input
                  type="range"
                  min={0}
                  max={4}
                  step={0.1}
                  value={strengthNegativePrompt}
                  onChange={(e) => setStrengthNegativePrompt(parseFloat(e.target.value))}
                  className="videoSound-slider"
                  data-testid="avatarfast-negative-strength"
                />
                <span className="videoSound-duration-value">{strengthNegativePrompt}</span>
              </div>
            )}

            {/* Seed + toggles */}
            <div className="nano-options-row avatarFast-options">
              <div className="nano-option">
                <span className="nano-option-label">Seed</span>
                <input
                  type="number"
                  value={seed}
                  onChange={(e) => setSeed(e.target.value)}
                  placeholder="Aleatorio"
                  className="nano-select avatarFast-seed-input nodrag nowheel"
                  data-testid="avatarfast-seed"
                />
              </div>
            </div>

            <div className="banana2pro-toggles">
              <label className="banana2pro-toggle avatarFast-toggle" title="Salta el chequeo de contenido del prompt y la imagen">
                <input type="checkbox" checked={disableSafetyFilter} onChange={(e) => setDisableSafetyFilter(e.target.checked)} data-testid="avatarfast-disable-safety" />
                <span>Sin filtro de seguridad</span>
              </label>
              <label className="banana2pro-toggle avatarFast-toggle" title="Usa la acción visual tal cual, sin mejora automática del modelo">
                <input type="checkbox" checked={disablePromptUpsampling} onChange={(e) => setDisablePromptUpsampling(e.target.checked)} data-testid="avatarfast-disable-upsample" />
                <span>Sin mejora de prompt</span>
              </label>
            </div>

            <button
              className="node-process-btn avatarFast-process-btn"
              onClick={handleProcess}
              disabled={!canProcess}
              title={requirementsTitle(runMissing)}
              data-testid="avatarfast-process-btn"
            >
              <Play size={14} />
              {data.outputUrl && reconfigure ? 'Regenerar con cambios' : 'Generar avatar'}
            </button>
            <NodeRequirements missing={runMissing} />
          </>
        )}

        <NodeError error={execError || localError} insufficientCredits={insufficientCredits} />
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle video-output-handle"
        isConnectable={!!data.hasOutput}
      />
    </div>
  );
});

AvatarFastNode.displayName = 'AvatarFastNode';

export default AvatarFastNode;
